import React from 'react';
import { Layout, Typography, Card, Row, Col, Button, Grid, Tag } from 'antd';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import {
    ShoppingCartOutlined,
    CarOutlined,
    CoffeeOutlined,
    CreditCardOutlined,
    ArrowLeftOutlined,
    CalculatorOutlined,
    FundOutlined,
} from '@ant-design/icons';
import {
    SIMULADORES_CATALOG,
    getSimuladorBySlug,
    resolveSimuladorSlug,
    SIMULADORES_REDIRECTS,
} from '../../utils/simuladores';
import CompararPrecosMercadoForm from './components/CompararPrecosMercadoForm';
import EtanolGasolinaForm from './components/EtanolGasolinaForm';
import DeliveryForm from './components/DeliveryForm';
import AvistaParceladoForm from './components/AvistaParceladoForm';
import QuantoGuardarForm from './components/QuantoGuardarForm';
import './SimuladoresPage.css';

const { Content } = Layout;
const { Title, Text, Paragraph } = Typography;
const { useBreakpoint } = Grid;

const ICONS = {
    ShoppingCartOutlined: <ShoppingCartOutlined />,
    CarOutlined: <CarOutlined />,
    CoffeeOutlined: <CoffeeOutlined />,
    CreditCardOutlined: <CreditCardOutlined />,
    FundOutlined: <FundOutlined />,
};

const FORMS = {
    'comparar-precos': CompararPrecosMercadoForm,
    'etanol-gasolina': EtanolGasolinaForm,
    delivery: DeliveryForm,
    'avista-parcelado': AvistaParceladoForm,
    'quanto-guardar': QuantoGuardarForm,
};

/** Lista plana para o grid do hub (evita uma seção por grupo com 1 card). */
const HUB_ITENS = SIMULADORES_CATALOG.flatMap((grupo) =>
    grupo.itens.map((item) => ({ ...item, grupo: grupo.grupo }))
);

function SimuladoresHub() {
    const navigate = useNavigate();
    const screens = useBreakpoint();
    const isMobile = !screens.md;

    return (
        <Content
            className={`simuladores-page${isMobile ? '' : ' simuladores-page--hub-desktop'}`}
            style={{ padding: isMobile ? '1rem' : '1.5rem 2rem' }}
        >
            <div className="simuladores-header">
                <Title level={isMobile ? 2 : 3} style={{ marginBottom: 4 }}>
                    <CalculatorOutlined style={{ marginRight: 8 }} />
                    Simuladores
                </Title>
                <Paragraph type="secondary" className="simuladores-header-desc">
                    Decisões rápidas do dia a dia — digite poucos números e veja se vale a pena antes de gastar.
                </Paragraph>
            </div>

            <Row gutter={[16, 16]} className="simuladores-hub-grid">
                {HUB_ITENS.map((item) => (
                    <Col xs={24} sm={12} lg={12} xl={6} key={item.slug}>
                        <Card
                            hoverable
                            className="simulador-card simulador-card--compact"
                            onClick={() => navigate(`/simuladores/${item.slug}`)}
                        >
                            <div className="simulador-card-body">
                                <div className="simulador-card-icon">
                                    {ICONS[item.icon] || <CalculatorOutlined />}
                                </div>
                                <div className="simulador-card-text">
                                    <Tag className="simulador-card-tag">{item.grupo}</Tag>
                                    <Title level={5} className="simulador-card-titulo">
                                        {item.titulo}
                                    </Title>
                                    <Text type="secondary" className="simulador-card-desc">
                                        {item.descricao}
                                    </Text>
                                </div>
                            </div>
                        </Card>
                    </Col>
                ))}
            </Row>
        </Content>
    );
}

function SimuladorDetalhe({ slug }) {
    const navigate = useNavigate();
    const screens = useBreakpoint();
    const isMobile = !screens.md;

    if (SIMULADORES_REDIRECTS[slug]) {
        return <Navigate to={`/simuladores/${SIMULADORES_REDIRECTS[slug]}`} replace />;
    }

    const resolvedSlug = resolveSimuladorSlug(slug);
    const meta = getSimuladorBySlug(resolvedSlug);
    const FormComponent = FORMS[resolvedSlug];

    if (!meta || !FormComponent) {
        return <Navigate to="/simuladores" replace />;
    }

    return (
        <Content className="simuladores-page" style={{ padding: isMobile ? '1rem' : '2rem' }}>
            <Button
                type="link"
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate('/simuladores')}
                style={{ paddingLeft: 0, marginBottom: 8 }}
            >
                Todos os simuladores
            </Button>
            <Title level={3} style={{ marginTop: 0, marginBottom: 4 }}>
                {meta.titulo}
            </Title>
            <Paragraph type="secondary" style={{ marginBottom: 20 }}>
                {meta.descricao}
            </Paragraph>
            <Card className="simulador-detalhe-card">
                <FormComponent />
            </Card>
        </Content>
    );
}

function SimuladoresPage() {
    const { slug } = useParams();
    if (slug) {
        return <SimuladorDetalhe slug={slug} />;
    }
    return <SimuladoresHub />;
}

export default SimuladoresPage;
